import React, { useEffect, useState } from 'react';

import useOrgSettingsStore from '../../stores/orgSettingsStore';
import { SETTINGS_COPY } from '../../constants/abdmCopy';

/**
 * HiuSettings — the per-org ABDM HIU (Health Information User) ID.
 *
 * Lives on the admin Settings page beside HipSettings (same card pattern),
 * gated by the page's own isAdmin check — a non-admin never reaches this
 * card. Single DB-backed value, read/written by BOTH directions of the live
 * HIU resolver (outbound consent/health-info requests + inbound webhook
 * routing — see app/abdm/webhooks/handlers/hiu/_org.py and app/api/hiu.py
 * on the backend). Distinct from the org's HIP ID (HipSettings) — HIU is
 * this org's identity when REQUESTING another facility's records (M3), HIP
 * is its identity when PUBLISHING its own (M2). Empty (the default) means
 * unset — the backend falls back to the sandbox env HIU ID until an admin
 * sets one here.
 */
export default function HiuSettings() {
  const abdmHiuId = useOrgSettingsStore((s) => s.abdmHiuId);
  const hiuLoaded = useOrgSettingsStore((s) => s.hiuLoaded);
  const fetchHiuSettings = useOrgSettingsStore((s) => s.fetchHiuSettings);
  const setHiuId = useOrgSettingsStore((s) => s.setHiuId);

  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!hiuLoaded) fetchHiuSettings();
  }, [hiuLoaded, fetchHiuSettings]);

  // Seed the input from the cached value once it loads.
  useEffect(() => {
    if (hiuLoaded) setValue(abdmHiuId || '');
  }, [hiuLoaded, abdmHiuId]);

  const trimmed = value.trim();
  const dirty = trimmed !== (abdmHiuId || '');

  const onSave = async () => {
    if (busy || !dirty) return;
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      await setHiuId(trimmed);
      setSaved(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || SETTINGS_COPY.saveError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-5 mt-5 shadow-sm">
      <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">{SETTINGS_COPY.hiuTitle}</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
        {SETTINGS_COPY.hiuText}
      </p>

      <div className="flex items-end gap-2 mt-3 flex-wrap">
        <div>
          <label htmlFor="hiu-id" className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
            HIU ID
          </label>
          <input
            id="hiu-id"
            type="text"
            value={value}
            disabled={busy}
            onChange={(e) => { setValue(e.target.value); setSaved(false); }}
            className="w-64 px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm disabled:opacity-60"
            data-testid="hiu-id-input"
          />
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={busy || !dirty}
          className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60"
          data-testid="hiu-id-save"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {saved && !dirty && (
          <span className="text-xs font-semibold text-teal-700 dark:text-teal-300 mb-2">Saved</span>
        )}
      </div>

      {err && <div className="text-xs text-rose-600 dark:text-rose-400 mt-2">{err}</div>}
    </div>
  );
}
